import { ChangeEvent, ReactNode, useState } from "react"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { makeStyles } from "tss-react/mui"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    FormControlLabel,
    Typography,
} from "@mui/material"
import fallbackSvgUrl from "../../../../../../shared/components/Formik/no-image.png"
import { borderTop } from "@mui/system"

const useStyles = makeStyles()((theme) => ({
    iconColor: {
        color: theme.palette.tailwind.slate[400],
    },
    accordion: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        boxShadow: "none",

        "&:first-of-type": {
            borderTopLeftRadius: "0.25rem",
            borderTopRightRadius: "0.25rem",
        },
        "&:last-of-type": {
            borderBottomLeftRadius: "0.25rem",
            borderBottomRightRadius: "0.25rem",
        },
        "&:before": {
            display: "none",
        },
        "&.Mui-expanded": {
            margin: 0,
        },
    },
    accordionSummary: {
        padding: "0.75rem",
        minHeight: "auto",
        "& .MuiAccordionSummary-content": {
            margin: `0`,
            "&.Mui-expanded": {
                margin: 0,
            },
        },
        "&.Mui-expanded": {
            minHeight: "auto",
        },
    },
    accordionDetails: {
        display: "grid",
        gap: "1rem",
        padding: "1rem 1.25rem 1rem 2.5rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    accordionDetailsBox: {
        display: "grid",
        gap: "1rem",
        padding: "0 0 2rem 0",
    },
    // Content
    content: {
        display: "grid",
        gridTemplateColumns: "1fr 10.75rem",
        gap: "2rem",
        justifyContent: "space-between",
    },
    img: {
        width: "100%",
        objectFit: "contain",
    },
    contentUpdate: {
        padding: "1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
}))

type CustomCheckboxAccordionProps = {
    name: string
    label: string
    checked: boolean
    onChange: (event: ChangeEvent<HTMLInputElement>) => void
    children: ReactNode
}

export const CustomCheckboxAccordion = ({ name, label, checked, onChange, children }: CustomCheckboxAccordionProps) => {
    const { classes } = useStyles()

    const [expanded, setExpanded] = useState(false)

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event)
    }

    const handleExpanded = () => {
        setExpanded((prev) => !prev)
    }
    return (
        <Accordion expanded={expanded} onChange={handleExpanded} className={classes.accordion}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                onClick={(event) => {
                    // Prevents accordion actions when clicking on the checkbox itself
                    event.stopPropagation()
                }}
                className={classes.accordionSummary}
            >
                <FormControlLabel
                    aria-label={label}
                    onClick={(event) => event.stopPropagation()}
                    onFocus={(event) => event.stopPropagation()}
                    control={<Checkbox checked={checked} onChange={handleChange} name={name} />}
                    label={<Typography>{label}</Typography>}
                />
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>{children}</AccordionDetails>
        </Accordion>
    )
}

type AccordionContentProps = {
    description?: string
    imageUrl?: string
}

export const AccordionContent = ({ description, imageUrl }: AccordionContentProps) => {
    const { classes } = useStyles()

    return (
        <Box className={classes.content}>
            <Typography variant="body2">
                {description ?? "This is a detailed description about the payment method."}
            </Typography>
            <img src={imageUrl || fallbackSvgUrl} className={classes.img} />
        </Box>
    )
}

type CustomAccordionProps = {
    title: ReactNode
    children: ReactNode
}

export const CustomAccordion = ({ title, children }: CustomAccordionProps) => {
    const { classes } = useStyles()

    const [expanded, setExpanded] = useState(false)

    const handleExpanded = () => {
        setExpanded((prev) => !prev)
    }
    return (
        <Accordion expanded={expanded} onChange={handleExpanded} className={classes.accordion}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                onClick={(event) => {
                    event.stopPropagation()
                }}
                className={classes.accordionSummary}
            >
                {title}
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetailsBox}>{children}</AccordionDetails>
        </Accordion>
    )
}

type CustomAccordionContentProps = {
    content: ReactNode
    imageUrl: string
}

export const CustomAccordionContent = ({ content, imageUrl }: CustomAccordionContentProps) => {
    const { classes, cx } = useStyles()

    return (
        <Box className={cx(classes.content, classes.contentUpdate)}>
            {content}
            <img src={imageUrl || fallbackSvgUrl} className={classes.img} />
        </Box>
    )
}
